import * as React from 'react';
import { useSectionAlignment } from './FmSectionAlignment';

export function isUnmodifiedAnchorActivation(event: React.MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

interface SurfaceAnchorAlignmentOptions {
  navigationRef: React.RefObject<HTMLElement | null>;
  itemIds: string;
  sticky: boolean;
  isDocked: boolean;
  navigationBehavior: 'anchor' | 'controlled';
}

export function useSurfaceAnchorAlignment({
  navigationRef,
  itemIds,
  sticky,
  isDocked,
  navigationBehavior,
}: SurfaceAnchorAlignmentOptions): (id: string) => void {
  const ownsAnchorAlignment = sticky && !isDocked && navigationBehavior === 'anchor';
  const getNavigation = React.useCallback(() => navigationRef.current, [navigationRef]);
  const getSections = React.useCallback(() => {
    const navigation = navigationRef.current;
    const reportSections = navigation
      ?.closest('.fm-report-page')
      ?.querySelector<HTMLElement>('.fm-report-page__sections');
    return reportSections ?? navigation?.parentElement ?? null;
  }, [navigationRef]);
  const alignSection = useSectionAlignment({
    getNavigation,
    getSections,
    revision: itemIds,
    enabled: ownsAnchorAlignment,
  });
  const lastRequestedAnchorRef = React.useRef<string | null>(null);
  const alignmentFrameRef = React.useRef(0);
  const requestAnchorAlignment = React.useCallback(
    (id: string): void => {
      if (!ownsAnchorAlignment) return;
      lastRequestedAnchorRef.current = id;
      window.cancelAnimationFrame(alignmentFrameRef.current);
      // Native href/history navigation runs first; this landing then remains
      // aligned while asynchronous sibling sections change the target's position.
      alignmentFrameRef.current = window.requestAnimationFrame(() => alignSection(id, 'auto'));
    },
    [alignSection, ownsAnchorAlignment],
  );
  React.useEffect(() => {
    if (!ownsAnchorAlignment) return undefined;
    const fromHash = (): void => {
      const id = window.location.hash.slice(1);
      if (!itemIds.split('|').includes(id) || lastRequestedAnchorRef.current === id) return;
      requestAnchorAlignment(id);
    };
    fromHash();
    window.addEventListener('hashchange', fromHash);
    return () => {
      window.cancelAnimationFrame(alignmentFrameRef.current);
      lastRequestedAnchorRef.current = null;
      window.removeEventListener('hashchange', fromHash);
    };
  }, [itemIds, ownsAnchorAlignment, requestAnchorAlignment]);

  return requestAnchorAlignment;
}
